import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import { ListColumns } from '../../widgets/ListColumns';
import { Summary } from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';

const backendServicesImg = require('../../assets/images/icon-illustration/back-end-services.svg');
const customSoftwareImg = require('../../assets/images/icon-illustration/custom-software.svg');
const enterpriseApplicationIntegrationImg = require('../../assets/images/icon-illustration/enterprise-application-integration.svg');
const enterpriseUXResearchImg = require('../../assets/images/icon-illustration/enterprise-ux-research.svg');
const reactDevelopmentImg = require('../../assets/images/illustration/react-development.svg');
const reactFeatureImg = require('../../assets/images/illustration/react-feature.svg');
const reactTechnologiesImg = require('../../assets/images/logo-illustration/react-technologies.svg');
const teamEfficiencyImg = require('../../assets/images/illustration/team-efficiency.svg');

import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import SitePenLogo from '../../widgets/SitepenLogo';

const factory = create();

export const ReactDevelopment = factory(function ReactDevelopment() {
	return (
		<div>
			<Head
				title="ReactJS Development Company | SitePen"
				description="ReactJS web development company building enterprise solutions to improve processes and empower business success."
			/>

			<Hero background="level3Blue">
				{{
					xlTitle: (
						<div>
							React
							<br />
							Development
						</div>
					),
					blurb:
						'React is a clean, efficient library for creating sophisticated data-driven user interfaces.'
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<SummaryList
						style="alternating-reverse"
						listGuttering="none"
						trailingBias="medium"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: 'Flexible React Development',
									blurb: (
										<virtual>
											<p>
												React started as a library for building
												sophisticated user interfaces for web applications
												in 2013. At that time, most JavaScript frameworks
												and libraries were focused on simplifying the
												modification of the web page's markup so that
												developers could keep the UI in sync with the
												application's state.
											</p>
											<p>
												React has established itself as a leading technology
												in creating web applications with complex, highly
												interactive user interfaces. React allows an
												application's interface to be broken down into small
												components. These components are automatically
												synchronized with the application's state. This
												synchronization frees developers from manually
												updating the page whenever something changes on the
												backend. By doing this, React allows developers to
												focus on the application's business logic rather
												than directly manipulating the HTML on the page.
											</p>
										</virtual>
									),
									image: <Image src={reactFeatureImg} size="lg" />
								},
								{
									blurb:
										"Sophisticated JavaScript-based applications are what SitePen is all about. Our team has extensive experience with all of today's development platforms, including React. This experience allows us to review each client's requirements and make informed recommendations for a technology stack that will provide the ideal balance between productivity, performance, and development time. Our experience allows us to recognize when libraries, such as React, enhance the development process or if other options might be better to consider.",
									image: <SitePenLogo />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'React Development Services',
							blurb: (
								<virtual>
									React's popularity has lead to a rich ecosystem of applications
									that can be built with it. We are ready to help you with
									whatever type of project you are looking to build.
								</virtual>
							),
							items: [
								{
									title: 'React Web Development',
									blurb:
										'When React was released in 2013, SitePen immediately saw how its reactive nature and uni-directional data flow could revolutionize web application development. Our team became familiar with its new paradigms and started to experiment with how React could be used to streamline the development process. We now have almost ten years of experience with this library and have used it extensively on its own and combined with related technologies such as Redux. Our deep understanding of the React ecosystem allows us to develop complex applications quickly and efficiently.',
									image: (
										<Image
											src={customSoftwareImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'React Native Development',
									blurb: (
										<virtual>
											<p>
												React has quickly become one of the leading
												technologies in the web application space due to its
												clean workflow and browser-agnostic architecture. In
												2015, React took on the additional challenge of
												providing a unified development experience to the
												mobile market with the introduction of React Native.
												While React Native appears to be very similar to the
												original, the changes required to run natively on
												mobile devices are substantial.
											</p>
											<p>
												Our development team took on the challenge of
												adopting this new platform by expanding upon the
												knowledge gained from using React on the web and
												combining it with our experience with native
												development for Android and iOS. The combination of
												these two skill sets has allowed us to leverage the
												productivity of React while still supporting the
												unique patterns that each platform has evolved. This
												allows true multi-platform mobile applications to be
												built with a very high degree of code reuse.
											</p>
										</virtual>
									),
									image: (
										<Image
											src={enterpriseUXResearchImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Custom React Development Services',
									blurb:
										'While the most common uses for React are to create web or mobile applications, SitePen can support many unique scenarios. We have worked with clients to integrate React into legacy applications to allow new features to be added with modern tools while leaving the existing code, with its complex business rules, untouched. We can also provide more targeted services, such as performance improvement projects and truly cross-platform development with code sharing between native and web applications.',
									image: (
										<Image
											src={backendServicesImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'React Enterprise Services',
									blurb:
										"Enterprise applications offer a unique set of challenges that React is very good at providing solutions for. These applications must often manage large amounts of disparate data and present them to the user differently, depending on what they are trying to accomplish. React's nature dramatically simplifies the creation of these data-heavy applications by allowing the application's state to be stored in a centralized location with the user-interface components automatically updating as that state evolves. React is a perfect fit when combined with a Flux-based state management system, such as Redux. Projects like Redux provide predictability and stability when working with large monolithic application states.",
									image: (
										<Image
											src={enterpriseApplicationIntegrationImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<Summary reverse stackedPadding={true}>
						{{
							title: 'Technologies We Use',
							blurb: (
								<virtual>
									<p>
										React is no longer just a single library. An entire
										ecosystem has evolved to augment and enhance React's feature
										set.
									</p>
									<ListColumns>
										<span>React</span>
										<span>Redux</span>
										<span>React Native</span>
										<span>Jest</span>
										<span>React Router</span>
										<span>Cypress</span>
									</ListColumns>
								</virtual>
							),
							image: <Image src={reactTechnologiesImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<IndustryExpertise />
			</Section>
			<Section>
				<ContentWrapper>
					<SummaryList
						style="alternating"
						listGuttering="none"
						trailingBias="medium"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: (
										<span>
											Choosing the Right React
											<br />
											Development Team
										</span>
									),
									blurb: (
										<virtual>
											<p>
												Few would dispute the idea that React has radically
												changed how web applications are built. The right
												React development team not only recognizes this new
												paradigm but embraces it fully while respecting its
												limitations.
											</p>
											<p>
												The best React developers understand how to leverage
												React's strengths to create fast, error-free,
												well-structured, and maintainable solutions. These
												teams use unidirectional data flows to centralize
												business rules into clear, easily tested modules
												that encourage reuse and future enhancement.
											</p>
											<p>
												The very best React development teams also
												understand the library's limitations and how best to
												overcome them. There are no perfect frameworks or
												libraries, and React is no exception. There are
												times when the most elegant implementation of a
												feature involves overriding some of React's
												patterns. The knowledge of when to do this is only
												gained through experience, both with React and the
												larger web application space.
											</p>
										</virtual>
									),
									image: <Image src={teamEfficiencyImg} lazy size="lg" />
								},

								{
									title: 'Benefits of Using React Development',
									blurb: (
										<virtual>
											<p>
												React has a relatively simple feature set, a fact
												that might be surprising given its tremendous
												popularity. The secret to unlocking React's power
												lies in the development team's ability to understand
												how to use reactive components to simplify the
												application's business logic and state management
												solutions.
											</p>
											<p>
												When used by an experienced team, React is an
												excellent choice for small- to medium-sized
												applications. It provides enough structure and
												guidance to allow applications of this size to be
												created with a high degree of confidence that the
												final product will be stable and open to future
												enhancement.
											</p>
											<p>
												Very small and very large applications might not be
												a good fit, however. For very small applications,
												React might be too heavy and force the application
												to become over-engineered. For very large
												applications, React might not be structured enough.
												Large applications often benefit from a more
												directed approach to organizing business logic and
												managing state than what React provides.
											</p>
										</virtual>
									),
									image: <Image src={reactDevelopmentImg} lazy size="lg" />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="React FAQs">
					{[
						{
							title: 'Why Should My Business Use React?',
							content: (
								<p>
									React is one of the top libraries for creating web applications
									and cross-platform mobile applications today. This popularity
									makes it relatively easy to find engineers with previous
									experience with it. Additionally, React's semi-opinionated
									nature makes it an ideal choice for many applications where some
									structure is valuable, but too much would lead to an
									over-engineered product.
								</p>
							)
						},
						{
							title: 'What Are Some Features of the React Library?',
							content: (
								<p>
									React's primary feature is its concept of reactive visual
									components. These components are composed together to make a
									user-interface that reflects the application's ever-changing
									internal state. This reactive nature simplifies development by
									allowing the development team to cleanly separate the
									application's business logic from its visual presentation.
								</p>
							)
						},
						{
							title: 'What’s the Benefit of Using React?',
							content: (
								<p>
									It has been said that the best libraries and frameworks make it
									obvious which solution is best. React is very good at guiding
									developers to write code that is highly performant and easily
									tested. Its use of reactive components, unidirectional
									dataflows, and centralized state management, when paired with
									Redux, lays the foundation for a stable application architecture
									that tends to be well structured and highly maintainable.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default ReactDevelopment;
